/**
 * Custom ComparisonBarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComparisonBarChartComponent as ParentComparisonBarChartComponent } from '@shared-lib/chart-components/comparison-bar-chart/comparison-bar-chart.component';

@Component({
  selector: 'app-comparison-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './comparison-bar-chart.component.html',
  styleUrls: ['./comparison-bar-chart.component.scss']
})
export class ComparisonBarChartComponent extends ParentComparisonBarChartComponent {
  chartTitle = '';
  chartSubtitle = '';
  unitBadge = '';
  legendItems: Array<{ label: string; color: string }> = [];
  summaryTitle = '';
  summaryText = '';
  footerMetrics: Array<{ label: string; value: any; kind?: string }> = [];
  tcoGroups: Array<{ label: string; subtitle?: string; values: number[]; comparisons?: Array<number | null> }> = [];
  tcoFormula: any = {};
  private readonly tcoMiniBarWidth = 14;
  private readonly tcoMiniBarGap = 2;
  private readonly tcoConnectorHeadroom = 34;
  private readonly tcoConnectorLevelGap = 12;
  private readonly tcoConnectorTopPadding = 3;

  override shouldShowConnector(): any {
    return super.shouldShowConnector();
  }

  override shouldShowComparisonBubbles(): any {
    // TODO: Add your custom logic here
    return super.shouldShowComparisonBubbles();
  }

  override shouldShowValues(): any {
    // TODO: Add your custom logic here
    return super.shouldShowValues();
  }

  override shouldShowLabels(): any {
    // TODO: Add your custom logic here
    return super.shouldShowLabels();
  }

  override getBottomLabel(): any {
    // TODO: Add your custom logic here
    return super.getBottomLabel();
  }

  override isFirstBarMode(): any {
    // TODO: Add your custom logic here
    return super.isFirstBarMode();
  }

  protected override onDataAssigned(): any {
    // TODO: Add your custom logic here
    return super.onDataAssigned();
  }

  protected override loadChartData(filters?: any): any {
    // TODO: Add your custom logic here
    return super.loadChartData(filters);
  }

  protected override extractChartData(response: any): any {
    const mappedResponse = this.mapTcoDashboardResponse(response) || response;
    const parsed = super.extractChartData(mappedResponse);
    const metadataSource = this.resolveMetadataSource(mappedResponse);

    return {
      ...parsed,
      ...this.extractMetadata(metadataSource),
    };
  }

  protected override resolveBaseData(response: any, dataKey: any): any {
    // TODO: Add your custom logic here
    return super.resolveBaseData(response, dataKey);
  }

  protected override bindDirectData(source: any): any {
    this.applyMetadata(source);
    return super.bindDirectData(source);
  }

  override prepareChart(source?: any): any {
    // TODO: Add your custom logic here
    return super.prepareChart(source);
  }

  override getBarHeight(value: any): any {
    // TODO: Add your custom logic here
    return super.getBarHeight(value);
  }

  override formatPercent(value: any, includePlus = true): any {
    return super.formatPercent(value, includePlus);
  }

  getPercentClass(value: any): string {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 'neutral';
    }

    if (numericValue < 0) {
      return 'positive';
    }

    if (numericValue > 0) {
      return 'negative';
    }

    return 'neutral';
  }

  getFooterMetricClass(metric: any): string {
    const kind = String(metric?.kind || metric?.type || '').toLowerCase();
    if (kind === 'confidence') {
      return `confidence ${String(metric?.value || '').toLowerCase()}`;
    }

    return this.getPercentClass(metric?.value);
  }

  hasTcoLayout(): boolean {
    return this.tcoGroups.length > 0;
  }

  getTcoGroup(key: string): any {
    const normalizedKey = String(key).toLowerCase();
    return this.tcoGroups.find((group) => String(group?.label || '').toLowerCase() === normalizedKey);
  }

  getTcoGroupByKey(key: any): any {
    if (typeof key === 'number') {
      return this.tcoGroups[key];
    }

    return this.getTcoGroup(String(key));
  }

  getNumeratorGroups(): any[] {
    const keys = Array.isArray(this.tcoFormula?.numerator)
      ? this.tcoFormula.numerator
      : this.tcoGroups.slice(0, 2).map((_, index) => index);

    return keys
      .map((key: any) => this.getTcoGroupByKey(key))
      .filter(Boolean);
  }

  getDenominatorGroup(): any {
    const key = this.tcoFormula?.denominator ?? 2;
    return this.getTcoGroupByKey(key);
  }

  getAdditiveGroups(): any[] {
    const keys = Array.isArray(this.tcoFormula?.additives)
      ? this.tcoFormula.additives
      : this.tcoGroups.slice(3, -1).map((_, index) => index + 3);

    return keys
      .map((key: any) => this.getTcoGroupByKey(key))
      .filter(Boolean);
  }

  getResultGroup(): any {
    const key = this.tcoFormula?.result ?? this.tcoGroups.length - 1;
    return this.getTcoGroupByKey(key);
  }

  getFormulaOperator(type: string, index = 0): string {
    const operators = this.tcoFormula?.operators || {};
    const configuredValue = operators?.[type];

    if (Array.isArray(configuredValue)) {
      return String(configuredValue[index] ?? '');
    }

    return String(configuredValue || (type === 'equals' ? '=' : '+'));
  }

  getMiniBarHeight(group: any, value: any): number {
    const numericValues = (group?.values || [])
      .map((item: any) => Number(item))
      .filter((item: number) => Number.isFinite(item));
    const max = numericValues.length ? Math.max(...numericValues) : 0;
    const numericValue = Number(value);

    if (!max || !Number.isFinite(numericValue)) {
      return 8;
    }

    const maxHeight = Number(this.data?.chartConfig?.miniBarMaxHeight ?? 58);
    const minHeight = Number(this.data?.chartConfig?.miniBarMinHeight ?? 10);

    return Math.max(minHeight, (numericValue / max) * maxHeight);
  }

  getMiniBarColor(index: number): string {
    return this.legendItems[index]?.color || this.getBarColor(index);
  }

  getTcoComparisons(group: any): number[] {
    const values = Array.isArray(group?.values) ? group.values : [];
    if (!values.length) {
      return [];
    }

    const configuredComparisons = Array.isArray(group?.comparisons)
      ? group.comparisons
          .map((value: any) => {
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? Math.round(numericValue) : null;
          })
      : [];

    if (configuredComparisons.length === values.length - 1) {
      return [0, ...configuredComparisons.map((value: number | null) => value ?? 0)];
    }

    if (configuredComparisons.length >= values.length) {
      return configuredComparisons
        .slice(0, values.length)
        .map((value: number | null) => value ?? 0);
    }

    const baseline = Number(values[0]);
    return values.map((value: any, index: number) => {
      const numericValue = Number(value);
      if (index === 0 || !Number.isFinite(baseline) || baseline === 0 || !Number.isFinite(numericValue)) {
        return 0;
      }

      return Math.round(((numericValue - baseline) / baseline) * 100);
    });
  }

  shouldShowTcoComparisons(group: any): boolean {
    return Array.isArray(group?.values) && group.values.length > 1;
  }

  getMiniBarAreaHeight(group: any): number {
    const configuredHeight = Number(this.data?.chartConfig?.miniBarAreaHeight);
    if (Number.isFinite(configuredHeight) && configuredHeight > 0) {
      return configuredHeight;
    }

    const label = String(group?.label || '').toLowerCase();
    if (label.includes('lto')) {
      return 54;
    }

    if (label.includes('fuel') || label.includes('total')) {
      return 56;
    }

    return 62;
  }

  getMiniChartHeight(group: any): number {
    return this.getMiniBarAreaHeight(group) + this.tcoConnectorHeadroom;
  }

  getMiniTrackWidth(group: any): number {
    const count = Math.max(1, Array.isArray(group?.values) ? group.values.length : 1);
    return (count * this.tcoMiniBarWidth) + ((count - 1) * this.tcoMiniBarGap);
  }

  getMiniBarCenterX(index: number): number {
    return (index * (this.tcoMiniBarWidth + this.tcoMiniBarGap)) + (this.tcoMiniBarWidth / 2);
  }

  getMiniConnectorWidth(index: number): number {
    return Math.max(0, index * (this.tcoMiniBarWidth + this.tcoMiniBarGap));
  }

  getMiniConnectorTop(group: any, index: number): number {
    const count = Math.max(1, Array.isArray(group?.values) ? group.values.length : 1);
    const branchCount = Math.max(1, count - 1);
    const levelFromTop = Math.max(0, branchCount - index);

    return this.tcoConnectorTopPadding + (levelFromTop * this.tcoConnectorLevelGap);
  }

  getMiniConnectorStemHeight(group: any, barIndex: number, connectorIndex: number): number {
    const values = Array.isArray(group?.values) ? group.values : [];
    const value = values[barIndex];
    const barTop = this.getMiniChartHeight(group) - this.getMiniBarHeight(group, value);
    const connectorTop = this.getMiniConnectorTop(group, connectorIndex);

    return Math.max(6, barTop - connectorTop);
  }

  formatMetricValue(metric: any): string {
    const kind = String(metric?.kind || metric?.type || '').toLowerCase();
    if (kind === 'confidence') {
      return String(metric?.value ?? '');
    }

    return this.formatPercent(Number(metric?.value ?? 0));
  }

  isConfidenceMetric(metric: any): boolean {
    return String(metric?.kind || metric?.type || '').toLowerCase() === 'confidence';
  }

  override hasComparison(index: any): any {
    // TODO: Add your custom logic here
    return super.hasComparison(index);
  }

  override getBarComparison(index: any): any {
    // TODO: Add your custom logic here
    return super.getBarComparison(index);
  }

  override getBarColor(index: any): any {
    // TODO: Add your custom logic here
    return super.getBarColor(index);
  }

  override getConnectorBottom(): any {
    // TODO: Add your custom logic here
    return super.getConnectorBottom();
  }

  override getBarTopBottom(index: any): any {
    // TODO: Add your custom logic here
    return super.getBarTopBottom(index);
  }

  override getPairTopBottom(index: any): any {
    // TODO: Add your custom logic here
    return super.getPairTopBottom(index);
  }

  override getTipBottom(index: any): any {
    // TODO: Add your custom logic here
    return super.getTipBottom(index);
  }

  override getTipHeight(index: any): any {
    // TODO: Add your custom logic here
    return super.getTipHeight(index);
  }

  override getTopPadding(): any {
    // TODO: Add your custom logic here
    return super.getTopPadding();
  }

  override getFirstBarBranchBottom(index: any): any {
    // TODO: Add your custom logic here
    return super.getFirstBarBranchBottom(index);
  }

  override getFirstBarBranchWidth(index: any): any {
    // TODO: Add your custom logic here
    return super.getFirstBarBranchWidth(index);
  }

  override getFirstBarTrunkHeight(): any {
    // TODO: Add your custom logic here
    return super.getFirstBarTrunkHeight();
  }

  override getFirstBarRightStemHeight(index: any): any {
    // TODO: Add your custom logic here
    return super.getFirstBarRightStemHeight(index);
  }

  override getFirstBarBubbleBottom(index: any): any {
    // TODO: Add your custom logic here
    return super.getFirstBarBubbleBottom(index);
  }

  override getLeftDropHeight(index: any): any {
    // TODO: Add your custom logic here
    return super.getLeftDropHeight(index);
  }

  override getRightDropHeight(index: any): any {
    // TODO: Add your custom logic here
    return super.getRightDropHeight(index);
  }

  override getBubbleBottom(index: any): any {
    // TODO: Add your custom logic here
    return super.getBubbleBottom(index);
  }

  override getBubbleOnConnectorBottom(): any {
    // TODO: Add your custom logic here
    return super.getBubbleOnConnectorBottom();
  }

  protected override getByPath(source: any, path: any): any {
    // TODO: Add your custom logic here
    return super.getByPath(source, path);
  }

  protected override extractArrayComparisons(groupData: any, comparisonBaseData: any, percentageKey: any, labelCount: any): any {
    // TODO: Add your custom logic here
    return super.extractArrayComparisons(groupData, comparisonBaseData, percentageKey, labelCount);
  }

  protected override extractObjectComparisons(groupData: any, comparisonBaseData: any, orderedKeys: any, percentageKey: any): any {
    // TODO: Add your custom logic here
    return super.extractObjectComparisons(groupData, comparisonBaseData, orderedKeys, percentageKey);
  }

  protected override readComparisonValue(entry: any, percentageKey: any): any {
    // TODO: Add your custom logic here
    return super.readComparisonValue(entry, percentageKey);
  }

  protected override normalizeComparisons(sourceComparisons: any, valuesLength: any): any {
    return super.normalizeComparisons(sourceComparisons, valuesLength);
  }

  private resolveMetadataSource(response: any): any {
    const responsePath = this.data?.responsePath;
    if (responsePath) {
      const source = this.getByPath(response, responsePath);
      if (source) {
        return source;
      }
    }

    return (
      response?.responseList?.[0]?.data?.[0] ||
      response?.responseList?.[0]?.data ||
      response?.data?.[0] ||
      response?.data ||
      response
    );
  }

  private mapTcoDashboardResponse(response: any): any | null {
    const source =
      response?.responseList?.[0]?.['ui-data']?.[0]?.data?.[0] ||
      response?.['ui-data']?.[0]?.data?.[0] ||
      null;

    if (!source || typeof source !== 'object' || !source.TotalTco) {
      return null;
    }

    const seriesOrder = ['CMI Actual', 'CMI Baseline', 'CAT Actual', 'MTU Actual'];
    const legendColors = ['#d90000', '#4d79e6', '#f6a000', '#203c63'];
    const totalTcoValues = this.extractSeriesValues(source.TotalTco, seriesOrder);
    const totalTcoVariances = this.extractFiniteComparisons(source.tco_dollar_hr_variance_pct, seriesOrder);
    const baselineVariance = Number(source?.tco_dollar_hr_variance_pct?.['CMI Baseline']);
    const competitorVariance = this.pickBestCompetitorVariance(
      source?.tco_dollar_hr_variance_pct,
      ['CAT Actual', 'MTU Actual'],
    );
    const hideConfidence = this.shouldHideConfidenceMetric();

    return {
      metadata: {
        chartTitle: 'Total Cost of Ownership',
        chartSubtitle: this.buildTcoSubtitle(source),
        unitBadge: '$/h',
        legendItems: seriesOrder.map((label, index) => ({
          label,
          color: legendColors[index],
        })),
        summaryTitle: 'TCO Summary',
        summaryText: this.buildTcoSummary(source),
        footerMetrics: [
          ...(Number.isFinite(baselineVariance)
            ? [{ label: 'vs. Baseline:', value: this.roundMetric(baselineVariance), kind: 'percentage' }]
            : []),
          ...(Number.isFinite(competitorVariance)
            ? [{ label: 'vs. Best Competitor:', value: this.roundMetric(competitorVariance), kind: 'percentage' }]
            : []),
          ...(hideConfidence
            ? []
            : [{ label: 'Confidence:', value: this.mapConfidence(source?.tcoScore), kind: 'confidence' }]),
        ],
        tcoFormula: {
          numerator: ['LCC($)', 'DEF($)'],
          denominator: 'LTO(h)',
          additives: ['Fuel($/h)'],
          result: 'Total TCO',
          operators: {
            numerator: ['+'],
            additive: '+',
            equals: '=',
          },
        },
        tcoGroups: [
          {
            label: 'LCC($)',
            values: this.extractSeriesValues(source.lcc, seriesOrder),
          },
          {
            label: 'DEF($)',
            values: this.extractSeriesValues(source.def, seriesOrder),
          },
          {
            label: 'LTO(h)',
            values: this.extractSeriesValues(source.total_Lto, seriesOrder),
          },
          {
            label: 'Fuel($/h)',
            values: this.extractSeriesValues(source.fuel, seriesOrder),
          },
          {
            label: 'Total TCO',
            subtitle: this.buildTotalTcoSubtitle(totalTcoValues[0]),
            values: totalTcoValues,
            comparisons: totalTcoVariances,
          },
        ],
      },
      overallChange: Number.isFinite(baselineVariance) ? baselineVariance : 0,
      groupData: seriesOrder.map((label, index) => ({
        label,
        value: totalTcoValues[index] ?? 0,
      })),
    };
  }

  private extractSeriesValues(source: any, seriesOrder: string[]): number[] {
    return seriesOrder.map((key) => {
      const value = Number(source?.[key]);
      return Number.isFinite(value) ? value : 0;
    });
  }

  private extractFiniteComparisons(source: any, seriesOrder: string[]): number[] {
    return seriesOrder
      .map((key) => Number(source?.[key]))
      .filter((value) => Number.isFinite(value))
      .map((value) => this.roundMetric(value));
  }

  private roundMetric(value: number): number {
    return Math.round(value);
  }

  private pickBestCompetitorVariance(source: any, keys: string[]): number {
    const candidates = keys
      .map((key) => Number(source?.[key]))
      .filter((value) => Number.isFinite(value));

    if (!candidates.length) {
      return NaN;
    }

    return candidates.reduce((best, current) =>
      Math.abs(current) < Math.abs(best) ? current : best,
    );
  }

  private buildTcoSubtitle(source: any): string {
    const customer = String(source?.customer_name || '').trim();
    const equipment = String(source?.equipment || '').trim();
    const engineModel = String(source?.engine_model_key || '').trim();
    const emission = this.formatEmissionLabel(source?.emission_key);

    return [customer, equipment, [engineModel, emission].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' - ');
  }

  private buildTcoSummary(source: any): string {
    const customer = String(source?.customer_name || 'the selected customer').trim();
    const equipment = String(source?.equipment || 'selected equipment').trim();
    const engineModel = String(source?.engine_model_key || 'engine option').trim();
    const emission = this.formatEmissionLabel(source?.emission_key) || 'selected emission';

    return `TCO comparison for ${customer} on ${equipment} using ${engineModel} and ${emission}.`;
  }

  private buildTotalTcoSubtitle(value: number): string {
    return Number.isFinite(value) ? `$${this.roundMetric(value)}/h` : '';
  }

  private mapConfidence(score: any): string {
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) {
      return 'Medium';
    }

    if (numericScore <= 1) {
      return 'High';
    }

    if (numericScore <= 2) {
      return 'Medium';
    }

    return 'Low';
  }

  private formatEmissionLabel(value: any): string {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }

    const compactTierMatch = raw.match(/^(\d+)T[_-]?TIER(\d+)$/i);
    if (compactTierMatch?.[2]) {
      return `Tier ${compactTierMatch[2]}`;
    }

    return raw.replace(/_/g, ' ');
  }

  private extractMetadata(source: any): any {
    const chartConfig = this.data?.chartConfig || {};
    const metadata = source?.metadata || source?.chartMetadata || source?.summary || source || {};

    return {
      chartTitle: this.readFirst(metadata, chartConfig, ['title', 'heading', 'chartTitle']),
      chartSubtitle: this.readFirst(metadata, chartConfig, ['subtitle', 'subTitle', 'chartSubtitle']),
      unitBadge: this.readFirst(metadata, chartConfig, ['unitBadge', 'unit', 'uom']),
      legendItems: this.extractLegendItems(metadata, chartConfig),
      summaryTitle: this.readFirst(metadata, chartConfig, ['summaryTitle'], 'TCO Summary'),
      summaryText: this.readFirst(metadata, chartConfig, ['summaryText', 'summary', 'description']),
      footerMetrics: this.extractFooterMetrics(metadata, chartConfig),
      tcoGroups: this.extractTcoGroups(metadata, chartConfig),
      tcoFormula: metadata?.tcoFormula || chartConfig?.tcoFormula || {},
    };
  }

  private applyMetadata(source: any): void {
    const metadata = this.extractMetadata(source);
    this.chartTitle = metadata.chartTitle || '';
    this.chartSubtitle = metadata.chartSubtitle || '';
    this.unitBadge = metadata.unitBadge || '';
    this.legendItems = metadata.legendItems || [];
    this.summaryTitle = metadata.summaryTitle || '';
    this.summaryText = metadata.summaryText || '';
    this.footerMetrics = metadata.footerMetrics || [];
    this.tcoGroups = metadata.tcoGroups || [];
    this.tcoFormula = metadata.tcoFormula || {};
  }

  private extractLegendItems(metadata: any, chartConfig: any): Array<{ label: string; color: string }> {
    const configuredLegend = metadata?.legendItems || metadata?.legend || chartConfig?.legendItems || chartConfig?.legend;
    if (Array.isArray(configuredLegend)) {
      return configuredLegend.map((item: any, index: number) => ({
        label: String(item?.label || item?.name || item || ''),
        color: String(item?.color || this.getBarColor(index)),
      })).filter((item) => item.label);
    }

    return (this.labels || []).map((label, index) => ({
      label,
      color: this.getBarColor(index),
    }));
  }

  private extractFooterMetrics(metadata: any, chartConfig: any): Array<{ label: string; value: any; kind?: string }> {
    const configuredMetrics = metadata?.footerMetrics || metadata?.metrics || chartConfig?.footerMetrics;
    if (Array.isArray(configuredMetrics)) {
      return configuredMetrics.map((metric: any) => ({
        label: String(metric?.label || metric?.name || ''),
        value: metric?.value,
        kind: metric?.kind || metric?.type,
      })).filter((metric) => metric.label && !(this.shouldHideConfidenceMetric() && this.isConfidenceMetric(metric)));
    }

    const baselineValue = this.readFirst(metadata, chartConfig, ['baselinePercentage', 'vsBaseline', 'baseline']);
    const competitorValue = this.readFirst(metadata, chartConfig, ['baseCompetitorPercentage', 'bestCompetitorPercentage', 'vsBestCompetitor', 'baseCompetitor']);
    const confidenceValue = this.readFirst(metadata, chartConfig, ['confidence', 'confidenceLevel']);
    const metrics: Array<{ label: string; value: any; kind?: string }> = [];

    if (baselineValue !== undefined && baselineValue !== '') {
      metrics.push({ label: 'vs. Baseline:', value: baselineValue, kind: 'percentage' });
    }

    if (competitorValue !== undefined && competitorValue !== '') {
      metrics.push({ label: 'vs. Best Competitor:', value: competitorValue, kind: 'percentage' });
    }

    if (!this.shouldHideConfidenceMetric() && confidenceValue !== undefined && confidenceValue !== '') {
      metrics.push({ label: 'Confidence:', value: confidenceValue, kind: 'confidence' });
    }

    return metrics;
  }

  private shouldHideConfidenceMetric(): boolean {
    const chartConfig = this.data?.chartConfig || {};
    return chartConfig.hideConfidence === true ||
      chartConfig.showConfidence === false ||
      chartConfig.hideConfidenceMetric === true;
  }

  private extractTcoGroups(metadata: any, chartConfig: any): Array<{ label: string; subtitle?: string; values: number[]; comparisons?: Array<number | null> }> {
    const configuredGroups = metadata?.tcoGroups || metadata?.tcoComponents || chartConfig?.tcoGroups || chartConfig?.tcoComponents;
    if (!Array.isArray(configuredGroups)) {
      return [];
    }

    return configuredGroups
      .map((group: any) => ({
        label: String(group?.label || group?.name || ''),
        subtitle: group?.subtitle,
        values: Array.isArray(group?.values)
          ? group.values.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
          : [],
        comparisons: Array.isArray(group?.comparisons)
          ? group.comparisons.map((value: any) => {
              const numericValue = Number(value);
              return Number.isFinite(numericValue) ? numericValue : null;
            })
          : [],
      }))
      .filter((group) => group.label && group.values.length);
  }

  private readFirst(primary: any, fallback: any, keys: string[], defaultValue: any = ''): any {
    for (const key of keys) {
      const value = primary?.[key] ?? fallback?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return defaultValue;
  }
}
